import React, { useState } from 'react';
import {
  Search,
  MapPin,
  Sliders,
  Sparkles,
  RotateCcw,
  AlertCircle,
  Building2,
  CheckCircle2
} from 'lucide-react';
import { searchLeads } from '../utils/api';
import ResultsTable from '../components/ResultsTable';
import { useToast } from '../hooks/useToast';

const QUICK_SEARCH_EXAMPLES = [
  'Home care products in Chandigarh',
  'Software development companies in Bangalore',
  'Dental clinics in Delhi',
  'Cafes and restaurants in Mumbai',
  'Boutique hotels in Goa'
];

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [numResults, setNumResults] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [searchedQuery, setSearchedQuery] = useState('');
  const toast = useToast();

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError('');
    setResults(null);
    setSearchedQuery(query.trim());

    try {
      const res = await searchLeads({
        query: query.trim(),
        location: location.trim(),
        num: numResults
      });

      const leadsData = res.data.data || [];
      setResults(leadsData);

      if (leadsData.length === 0) {
        toast('No matching leads found. Try refining your search query.', 'error');
      } else {
        toast(`Extracted ${leadsData.length} leads successfully!`, 'success');
      }

      if (res.data.warning) {
        toast(res.data.warning, 'error');
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Extraction failed. Please check your SerpAPI key in the Admin Panel.';
      setError(errMsg);
      toast(errMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setQuery('');
    setLocation('');
    setResults(null);
    setError('');
    setSearchedQuery('');
  };

  return (
    <div>
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-pill">
          <span className="hero-pill-status" />
          <span>SerpAPI Real-time Google Maps Engine</span>
        </div>

        <h1 className="hero-title">
          Lead Discovery & Data Extractor
        </h1>
        <p className="hero-subtitle">
          Extract verified company names, contact numbers, websites, ratings, and addresses directly into Excel.
        </p>

        {/* Search Card */}
        <form onSubmit={handleSearch} className="search-card">
          <div className="search-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="search-query">
                <Search size={14} color="var(--color-indigo)" />
                Search Query *
              </label>
              <input
                id="search-query"
                type="text"
                className="form-input"
                placeholder="e.g. Home care products in Chandigarh"
                value={query}
                onChange={e => setQuery(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="search-location">
                <MapPin size={14} color="var(--text-muted)" />
                Location Coordinates (Optional)
              </label>
              <input
                id="search-location"
                type="text"
                className="form-input"
                placeholder="e.g. @30.7333,76.7794,14z"
                value={location}
                onChange={e => setLocation(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="num-results">
                <Sliders size={14} color="var(--text-muted)" />
                Target Leads
              </label>
              <select
                id="num-results"
                className="form-input"
                value={numResults}
                onChange={e => setNumResults(parseInt(e.target.value, 10))}
                style={{ cursor: 'pointer' }}
              >
                <option value={10}>10 Leads</option>
                <option value={20}>20 Leads</option>
                <option value={40}>40 Leads</option>
                <option value={60}>60 Leads</option>
              </select>
            </div>
          </div>

          <div className="search-actions">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                id="search-btn"
                type="submit"
                className="btn btn-primary"
                disabled={loading || !query.trim()}
              >
                {loading ? (
                  <>
                    <span className="spinner" />
                    Extracting Leads...
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    Extract Leads
                  </>
                )}
              </button>

              {(results || query) && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClear}
                >
                  <RotateCcw size={14} />
                  Reset
                </button>
              )}
            </div>

            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Auto key rotation active
            </span>
          </div>
        </form>

        {/* Quick query chips */}
        {!results && !loading && (
          <div className="quick-queries">
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Suggestions:</span>
            {QUICK_SEARCH_EXAMPLES.map((ex) => (
              <button
                key={ex}
                type="button"
                className="quick-query-chip"
                onClick={() => setQuery(ex)}
              >
                {ex}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error Notice */}
      {error && (
        <div style={{
          maxWidth: 880,
          margin: '0 auto 1.5rem',
          padding: '0.85rem 1.25rem',
          borderRadius: 'var(--radius-sm)',
          backgroundColor: 'var(--color-danger-subtle)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          color: 'var(--color-danger)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.65rem',
          fontSize: '0.85rem'
        }}>
          <AlertCircle size={16} style={{ flexShrink: 0 }} />
          <span>{error}</span>
        </div>
      )}

      {/* Extracted Leads Table */}
      {results && (
        <ResultsTable
          results={results}
          query={searchedQuery}
        />
      )}
    </div>
  );
}
